import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { eachMonthOfInterval, endOfMonth, format, startOfMonth, subMonths } from 'date-fns';
import { Truck, Train, Plane, Ship, ArrowRight, Sparkles } from 'lucide-react';
import { usePalette, ChartTooltip, axisProps, fmtNumber, fmtAxis, fmtShare } from '../charts/chartKit';

/**
 * Dashboard overview in the approved concept layout:
 *   [ Monthly emissions by category (stacked) | Freight by transport mode ]
 *   [ Top routes table                        | Recent shipments table    ]
 * Everything is computed from data the dashboard already loads (shipments + Scope 3 activities).
 */
const CATS = [
  { cat: 4, key: 'c4', label: 'Cat 4 · Upstream' },
  { cat: 9, key: 'c9', label: 'Cat 9 · Downstream' },
  { cat: 6, key: 'c6', label: 'Cat 6 · Business travel' },
  { cat: 7, key: 'c7', label: 'Cat 7 · Commuting' },
];
const MODES = {
  road: { name: 'Road', icon: Truck },
  rail: { name: 'Rail', icon: Train },
  air: { name: 'Air', icon: Plane },
  water: { name: 'Sea', icon: Ship },
};
const CAT_OF_GOOD = { upstream: 4, downstream: 9 };

const t = (kg) => (Number(kg) || 0) / 1000; // kg -> tonnes

function Card({ title, description, right, children, className = '' }) {
  return (
    <section className={`rounded-xl border bg-card shadow ${className}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 px-5 pt-5 sm:px-6">
        <div className="min-w-0">
          <h3 className="text-[15px] font-semibold leading-tight text-foreground">{title}</h3>
          {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
        </div>
        {right}
      </div>
      <div className="px-5 pb-5 pt-4 sm:px-6">{children}</div>
    </section>
  );
}

function CatTag({ cat, palette }) {
  if (!cat) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
      <span className="h-2 w-2 rounded-[2px]" style={{ background: palette.category(cat) }} aria-hidden="true" />
      Cat {cat}
    </span>
  );
}

function ModeTag({ mode }) {
  const m = MODES[mode];
  if (!m) return <span className="text-muted-foreground">{mode || '—'}</span>;
  const Icon = m.icon;
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {m.name}
    </span>
  );
}

export default function DashboardOverview({ shipments = [], allShipments = [], activities = [] }) {
  const palette = usePalette();

  // Last 12 months, tonnes per category (freight by shipment date, travel/commute by activity date)
  const monthly = useMemo(() => {
    const now = new Date();
    return eachMonthOfInterval({ start: subMonths(now, 11), end: now }).map((m) => {
      const a = startOfMonth(m), b = endOfMonth(m);
      const inMonth = (iso) => { const d = new Date(iso); return d >= a && d <= b; };
      const row = { month: format(m, 'MMM'), label: format(m, 'MMM yyyy'), c4: 0, c9: 0, c6: 0, c7: 0 };
      allShipments.forEach((s) => {
        if (!inMonth(s.created_at)) return;
        row.c4 += t(s.upstream_emissions);
        row.c9 += t(s.downstream_emissions);
      });
      activities.forEach((x) => {
        if (!inMonth(x.activity_date || x.created_at)) return;
        if (Number(x.ghg_category) === 6) row.c6 += t(x.co2e_kg);
        if (Number(x.ghg_category) === 7) row.c7 += t(x.co2e_kg);
      });
      row.total = row.c4 + row.c9 + row.c6 + row.c7;
      return row;
    });
  }, [allShipments, activities]);
  const hasMonthly = monthly.some((r) => r.total > 0);

  // Freight by mode (Cat 4 + 9), tonnes
  const modes = useMemo(() => {
    const acc = {};
    shipments.forEach((s) => (s.transport_legs || []).forEach((leg) => {
      const k = leg.transport_mode;
      acc[k] = (acc[k] || 0) + (Number(leg.emissions) || 0);
    }));
    return Object.entries(acc).filter(([, v]) => v > 0).map(([mode, kg]) => ({ mode, kg })).sort((x, y) => y.kg - x.kg);
  }, [shipments]);
  const modeTotal = modes.reduce((s, m) => s + m.kg, 0);
  const top = modes[0];

  // Top routes by emissions
  const routes = useMemo(() => {
    const acc = {};
    shipments.forEach((s) => (s.transport_legs || []).forEach((leg) => {
      const from = leg.from_location?.address?.split(',')[0];
      const to = leg.to_location?.address?.split(',')[0];
      if (!from || !to) return;
      const key = `${from}→${to}·${leg.transport_mode}`;
      acc[key] = acc[key] || { from, to, mode: leg.transport_mode, trips: 0, kg: 0 };
      acc[key].trips += Number(leg.trips) || 1;
      acc[key].kg += Number(leg.emissions) || 0;
    }));
    return Object.values(acc).sort((a, b) => b.kg - a.kg).slice(0, 5);
  }, [shipments]);

  const recent = useMemo(
    () => [...shipments].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at))).slice(0, 5),
    [shipments]
  );

  return (
    <div className="space-y-6">
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.6fr)_minmax(300px,1fr)]">
        <Card title="Monthly emissions by category" description="t CO₂e · last 12 months · hover a month for the breakdown">
          <ul className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {CATS.map((c) => (
              <li key={c.cat} className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: palette.category(c.cat) }} aria-hidden="true" />
                {c.label}
              </li>
            ))}
          </ul>
          {hasMonthly ? (
            <div className="h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthly} margin={{ top: 8, right: 4, left: 0, bottom: 0 }} barCategoryGap="28%">
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="month" {...axisProps} />
                  <YAxis {...axisProps} tickFormatter={fmtAxis} width={40} />
                  <Tooltip
                    cursor={{ fill: 'hsl(var(--muted))', opacity: 0.6 }}
                    content={(
                      <ChartTooltip
                        title={(p) => p[0]?.payload?.label}
                        rows={(p) => {
                          const r = p[0].payload;
                          return [...CATS].reverse().map((c) => ({ label: c.label, value: `${fmtNumber(r[c.key], 2)} t`, color: palette.category(c.cat) }))
                            .concat([{ label: 'Total', value: `${fmtNumber(r.total, 2)} t` }]);
                        }}
                      />
                    )}
                  />
                  {CATS.map((c, i) => (
                    <Bar
                      key={c.key}
                      dataKey={c.key}
                      stackId="m"
                      name={c.label}
                      fill={palette.category(c.cat)}
                      stroke="hsl(var(--card))"
                      strokeWidth={i === CATS.length - 1 ? 0 : 2}
                      radius={i === CATS.length - 1 ? [4, 4, 0, 0] : 0}
                      isAnimationActive={palette.animate}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="py-10 text-center text-sm text-muted-foreground">No emissions recorded in the last 12 months.</p>
          )}
        </Card>

        <Card title="Freight by transport mode" description="Cat 4 + Cat 9 · t CO₂e">
          {modes.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">No freight shipments yet.</p>
          ) : (
            <ul className="space-y-4">
              {modes.map(({ mode, kg }) => {
                const m = MODES[mode] || { name: mode, icon: Truck };
                const Icon = m.icon;
                return (
                  <li key={mode} className="grid grid-cols-[84px_1fr_72px] items-center gap-3 text-sm">
                    <span className="flex items-center gap-2 font-medium text-foreground">
                      <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />{m.name}
                    </span>
                    <span className="h-2.5 overflow-hidden rounded-full bg-muted" aria-hidden="true">
                      <span className="block h-full rounded-[4px]" style={{ width: `${Math.max(1.5, (kg / modes[0].kg) * 100)}%`, background: palette.mode(mode) }} />
                    </span>
                    <span className="text-right tabular-nums">
                      <span className="block font-semibold text-foreground">{fmtNumber(t(kg), 2)}</span>
                      <span className="block text-xs text-muted-foreground">{fmtShare(kg, modeTotal)}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          {top && (
            <div className="mt-5 flex items-start gap-2.5 rounded-2xl bg-accent px-3.5 py-3 text-sm text-foreground">
              <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" aria-hidden="true" />
              <span>
                {MODES[top.mode]?.name || top.mode} carries <b className="tabular-nums">{fmtShare(top.kg, modeTotal)}</b> of your freight emissions.{' '}
                <Link to="/shanthi" className="font-semibold text-primary hover:underline">Ask Trace</Link> how to reduce it.
              </span>
            </div>
          )}
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card title="Top routes" description="By total emissions">
          {routes.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Routes appear once shipments have an origin and destination.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    <th className="pb-2 pr-3 font-semibold">Route</th>
                    <th className="pb-2 pr-3 font-semibold">Mode</th>
                    <th className="pb-2 pr-3 text-right font-semibold">Trips</th>
                    <th className="pb-2 text-right font-semibold">t CO₂e</th>
                  </tr>
                </thead>
                <tbody>
                  {routes.map((r) => (
                    <tr key={`${r.from}-${r.to}-${r.mode}`} className="border-t border-border/70">
                      <td className="py-2.5 pr-3 font-medium text-foreground">
                        <span className="inline-flex items-center gap-2 whitespace-nowrap">{r.from}<ArrowRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />{r.to}</span>
                      </td>
                      <td className="py-2.5 pr-3"><ModeTag mode={r.mode} /></td>
                      <td className="py-2.5 pr-3 text-right tabular-nums">{r.trips}</td>
                      <td className="py-2.5 text-right font-semibold tabular-nums text-foreground">{fmtNumber(t(r.kg), 2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card
          title="Recent shipments"
          description="Latest calculations"
          right={<a href="#all-shipments" className="text-sm font-semibold text-primary hover:underline">View all</a>}
        >
          {recent.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No shipments yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    <th className="pb-2 pr-3 font-semibold">Goods</th>
                    <th className="pb-2 pr-3 font-semibold">Category</th>
                    <th className="pb-2 pr-3 font-semibold">Date</th>
                    <th className="pb-2 text-right font-semibold">kg CO₂e</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((s) => (
                    <tr key={s.id} className="border-t border-border/70">
                      <td className="py-2.5 pr-3 text-foreground">
                        {s.good?.name || '—'}
                        <span className="text-muted-foreground"> · {fmtNumber(s.good?.quantity, 2)} {s.good?.unit === 'tons' ? 't' : s.good?.unit}</span>
                      </td>
                      <td className="py-2.5 pr-3"><CatTag cat={CAT_OF_GOOD[s.good?.ghg_category]} palette={palette} /></td>
                      <td className="whitespace-nowrap py-2.5 pr-3 text-muted-foreground">
                        {s.created_at ? format(new Date(s.created_at), 'dd MMM yyyy') : '—'}
                      </td>
                      <td className="py-2.5 text-right font-semibold tabular-nums text-foreground">{fmtNumber(s.total_emissions, 1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
